import { Controller } from "@hotwired/stimulus";
import SignaturePad from "signature_pad";
import { dsConfirm } from "../../utils/ds_confirm";

/**
 * Stimulus controller for managing school signatures and stamps
 * Handles both drawing signatures and uploading images
 *
 * @example HTML Usage
 * <div data-controller="signature-stamp"
 *      data-signature-stamp-max-size-value="5242880"
 *      data-signature-stamp-allowed-types-value='["image/png","image/jpeg","image/tiff","image/heic"]'>
 *   <canvas data-signature-stamp-target="canvas"></canvas>
 *   <input type="file" data-signature-stamp-target="fileInput">
 *   <img data-signature-stamp-target="preview">
 * </div>
 */
export default class extends Controller {
  static targets = [
    "canvas",
    "fileInput",
    "preview",
    "previewImage",
    "drawTab",
    "uploadTab",
    "drawPanel",
    "uploadPanel",
    "dropZone",
    "fileName",
    "clearBtn",
    "existingImage",
  ];

  static values = {
    maxSize: { type: Number, default: 5242880 }, // 5MB in bytes
    allowedTypes: {
      type: Array,
      default: ["image/png", "image/jpeg", "image/tiff", "image/heic"],
    },
    fieldName: { type: String, default: "signature_image" },
    errorMessageSize: {
      type: String,
      default: "Le fichier est trop volumineux (max 5MB)",
    },
    errorMessageType: {
      type: String,
      default: "Format non supporté. Utilisez PNG, JPEG, TIFF ou HEIC",
    },
  };

  connect() {
    this.signaturePad = null;
    this.currentTab = "draw";
    this.hasDrawnSignature = false;

    // Initialize signature pad if canvas exists
    if (this.hasCanvasTarget) {
      this.initializeSignaturePad();
    }

    // Setup drag and drop
    if (this.hasDropZoneTarget) {
      this.setupDragAndDrop();
    }

    // Show draw tab by default
    this.switchTab("draw");
  }

  disconnect() {
    if (this.signaturePad) {
      this.signaturePad.off();
    }
  }

  /**
   * Initialize the SignaturePad library on the canvas
   */
  initializeSignaturePad() {
    const canvas = this.canvasTarget;
    const container = canvas.parentElement;

    // Set canvas size
    canvas.width = container.offsetWidth || 400;
    canvas.height = 200;

    this.signaturePad = new SignaturePad(canvas, {
      penColor: "rgb(0, 0, 0)",
      backgroundColor: "rgba(255, 255, 255, 0)", // Transparent background
      minWidth: 1,
      maxWidth: 3,
    });

    // Listen for stroke end to update state
    this.signaturePad.addEventListener("endStroke", () => {
      this.hasDrawnSignature = !this.signaturePad.isEmpty();
      this.updateUI();
    });
  }

  /**
   * Setup drag and drop functionality for file upload
   */
  setupDragAndDrop() {
    const dropZone = this.dropZoneTarget;

    ["dragenter", "dragover", "dragleave", "drop"].forEach((eventName) => {
      dropZone.addEventListener(eventName, (e) => {
        e.preventDefault();
        e.stopPropagation();
      });
    });

    ["dragenter", "dragover"].forEach((eventName) => {
      dropZone.addEventListener(eventName, () => {
        dropZone.classList.add("border-blue-500", "bg-blue-50");
      });
    });

    ["dragleave", "drop"].forEach((eventName) => {
      dropZone.addEventListener(eventName, () => {
        dropZone.classList.remove("border-blue-500", "bg-blue-50");
      });
    });

    dropZone.addEventListener("drop", (e) => {
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.handleFileSelect(files[0]);
      }
    });
  }

  /**
   * Switch between Draw and Upload tabs
   * @param {Event|string} eventOrTab - Click event or tab name
   */
  switchTab(eventOrTab) {
    const tab =
      typeof eventOrTab === "string"
        ? eventOrTab
        : eventOrTab.currentTarget.dataset.tab;

    this.currentTab = tab;

    // Update tab buttons with Tailwind classes
    if (this.hasDrawTabTarget && this.hasUploadTabTarget) {
      // Active tab classes
      const activeClasses = ["border-cta", "text-cta"];
      // Inactive tab classes
      const inactiveClasses = ["border-transparent", "text-gray-500"];

      if (tab === "draw") {
        this.drawTabTarget.classList.remove(...inactiveClasses);
        this.drawTabTarget.classList.add(...activeClasses);
        this.uploadTabTarget.classList.remove(...activeClasses);
        this.uploadTabTarget.classList.add(...inactiveClasses);
      } else {
        this.uploadTabTarget.classList.remove(...inactiveClasses);
        this.uploadTabTarget.classList.add(...activeClasses);
        this.drawTabTarget.classList.remove(...activeClasses);
        this.drawTabTarget.classList.add(...inactiveClasses);
      }
    }

    // Update panels
    if (this.hasDrawPanelTarget && this.hasUploadPanelTarget) {
      this.drawPanelTarget.classList.toggle("hidden", tab !== "draw");
      this.uploadPanelTarget.classList.toggle("hidden", tab !== "upload");
    }

    // Resize canvas when switching to draw tab
    if (tab === "draw" && this.signaturePad) {
      requestAnimationFrame(() => this.resizeCanvas());
    }
  }

  /**
   * Resize canvas to fit container
   */
  resizeCanvas() {
    if (!this.hasCanvasTarget || !this.signaturePad) return;

    const canvas = this.canvasTarget;
    const container = canvas.parentElement;
    const data = this.signaturePad.toData();

    canvas.width = container.offsetWidth || 400;
    canvas.height = 200;

    this.signaturePad.clear();
    if (data.length > 0) {
      this.signaturePad.fromData(data);
    }
  }

  /**
   * Clear the signature pad
   */
  clear() {
    if (this.signaturePad) {
      this.signaturePad.clear();
      this.hasDrawnSignature = false;
    }

    if (this.hasFileInputTarget) {
      this.fileInputTarget.value = "";
    }

    this.hidePreview();
    this.updateUI();
  }

  /**
   * Handle file input change
   * @param {Event} event
   */
  onFileChange(event) {
    const file = event.target.files[0];
    if (file) {
      this.handleFileSelect(file);
    }
  }

  /**
   * Handle file selection (from input or drag & drop)
   * @param {File} file
   */
  handleFileSelect(file) {
    // Validate file size
    if (file.size > this.maxSizeValue) {
      this.showError(this.errorMessageSizeValue);
      return;
    }

    // Validate file type
    if (!this.allowedTypesValue.includes(file.type)) {
      this.showError(this.errorMessageTypeValue);
      return;
    }

    this.hideError();

    // Update file input
    if (this.hasFileInputTarget) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      this.fileInputTarget.files = dataTransfer.files;
    }

    // Show file name
    if (this.hasFileNameTarget) {
      this.fileNameTarget.textContent = file.name;
      this.fileNameTarget.classList.remove("hidden");
    }

    // Show preview
    this.showPreview(file);
  }

  /**
   * Show preview of the selected/drawn image
   * @param {File|Blob} file
   */
  showPreview(file) {
    if (!this.hasPreviewTarget || !this.hasPreviewImageTarget) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      this.previewImageTarget.src = e.target.result;
      this.previewTarget.classList.remove("hidden");
    };
    reader.readAsDataURL(file);
  }

  /**
   * Hide the preview
   */
  hidePreview() {
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add("hidden");
    }
    if (this.hasFileNameTarget) {
      this.fileNameTarget.classList.add("hidden");
    }
  }

  /**
   * Convert signature canvas to file and attach to input
   */
  async saveSignatureToInput() {
    if (!this.signaturePad || this.signaturePad.isEmpty()) {
      return false;
    }

    try {
      const signatureB64 = this.signaturePad.toDataURL("image/png");
      const signatureFile = await this.dataURLtoFile(
        signatureB64,
        "signature.png",
        "image/png",
      );

      if (this.hasFileInputTarget) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(signatureFile);
        this.fileInputTarget.files = dataTransfer.files;
      }

      return true;
    } catch (error) {
      console.error("Error saving signature:", error);
      return false;
    }
  }

  /**
   * Convert data URL to File object
   * @param {string} dataUrl
   * @param {string} filename
   * @param {string} mimeType
   * @returns {Promise<File>}
   */
  async dataURLtoFile(dataUrl, filename, mimeType) {
    const res = await fetch(dataUrl);
    const buf = await res.arrayBuffer();
    return new File([buf], filename, { type: mimeType });
  }

  /**
   * Handle form submission - save signature if in draw mode
   * @param {Event} event
   */
  async beforeSubmit(event) {
    if (this.currentTab === "draw" && this.hasDrawnSignature) {
      const saved = await this.saveSignatureToInput();
      if (!saved) {
        event.preventDefault();
        this.showError("Erreur lors de la sauvegarde de la signature");
      }
    }
  }

  /**
   * Show error message
   * @param {string} message
   */
  showError(message) {
    const errorEl = this.element.querySelector("[data-signature-error]");
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.remove("hidden");
    } else {
      alert(message);
    }
  }

  /**
   * Hide error message
   */
  hideError() {
    const errorEl = this.element.querySelector("[data-signature-error]");
    if (errorEl) {
      errorEl.classList.add("hidden");
    }
  }

  /**
   * Update UI state based on current state
   */
  updateUI() {
    // Show/hide clear button
    if (this.hasClearBtnTarget) {
      this.clearBtnTarget.classList.toggle(
        "hidden",
        !this.hasDrawnSignature && !this.hasFileInputTarget?.files?.length,
      );
    }
  }

  /**
   * Trigger file input click
   */
  triggerFileInput() {
    if (this.hasFileInputTarget) {
      this.fileInputTarget.click();
    }
  }

  /**
   * Remove existing image via AJAX
   * @param {Event} event
   */
  async removeExisting(event) {
    event.preventDefault();
    const url = event.currentTarget.dataset.url;
    const attachment = event.currentTarget.dataset.attachment;

    if (!(await dsConfirm("Voulez-vous vraiment supprimer cette image ?", { tone: "danger" }))) {
      return;
    }

    try {
      const response = await fetch(`${url}?attachment=${attachment}`, {
        method: "DELETE",
        headers: {
          Accept: "text/vnd.turbo-stream.html, application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            ?.content,
        },
      });

      if (response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("turbo-stream")) {
          const html = await response.text();
          Turbo.renderStreamMessage(html);
        } else {
          // Hide the existing image container
          if (this.hasExistingImageTarget) {
            this.existingImageTarget.classList.add("hidden");
          }
        }
      } else {
        this.showError("Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Error removing image:", error);
      this.showError("Erreur lors de la suppression");
    }
  }
}
